import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider } from "./contexts/I18nContext";
import ProtectedRoute from "./components/app/ProtectedRoute";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import PlayerDashboard from "./pages/PlayerDashboard";
import ChildrenPage from "./pages/Children";
import ChildProfile from "./pages/ChildProfile";
import CalendarPage from "./pages/CalendarPage";
import ChatPage from "./pages/Chat";
import MatchesPage from "./pages/Matches";
import CoachPublicProfile from "./pages/CoachPublicProfile";
import CoachSettings from "./pages/CoachSettings";
import SettingsRouter from "./pages/SettingsRouter";
import GoalkeeperCoaches from "./pages/GoalkeeperCoaches";
import BookingsPage from "./pages/BookingsPage";
import CoachesMap from "./pages/CoachesMap";
import AdminPanel from "./pages/AdminPanel";
import FavoritesPage from "./pages/FavoritesPage";
import AcademyDashboard from "./pages/AcademyDashboard";
import AcademyPublicProfile from "./pages/AcademyPublicProfile";
import ParentDashboard from "./pages/ParentDashboard";
import PlayerProfile from "./pages/PlayerProfile";
import ClaimInvite from "./pages/ClaimInvite";
import AcademyCoachesPage from "./pages/AcademyCoachesPage";
import AcademyApplicationsPage from "./pages/AcademyApplicationsPage";
import { SiteProvider } from "./contexts/SiteContext";
import SiteHome from "./pages/site/Home";
import SiteSearch from "./pages/site/Search";
import SiteCoachProfile from "./pages/site/CoachProfile";
import SiteBook from "./pages/site/Book";
import SiteForCoaches, { Policies } from "./pages/site/ForCoaches";
import EarningsPage from "./pages/EarningsPage";
import AcademySubscriptionPage from "./pages/AcademySubscriptionPage";
import AcademyProgramsPage from "./pages/AcademyProgramsPage";
import CustomersPage from "./pages/CustomersPage";
import { Toaster } from "sonner";

function RoleHome() {
  const { user } = useAuth();
  if (user?.role === "player") return <PlayerDashboard />;
  if (user?.role === "academy") return <AcademyDashboard />;
  if (user?.role === "parent") return <ParentDashboard />;
  if (user?.role === "admin") return <Navigate to="/admin" replace />;
  return <Dashboard />;
}

function UpgradeRedirect() {
  const { user } = useAuth();
  return <Navigate to={user?.role === "academy" ? "/academy/subscription" : "/dashboard"} replace />;
}

function RootRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <SiteHome />;
  return <RoleHome />;
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
      <SiteProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/brand-preview" element={<Navigate to="/" replace />} />
            <Route path="/preview/*" element={<Navigate to="/" replace />} />
            <Route path="/sports/:sport" element={<SiteSearch />} />
            <Route path="/book/:id" element={<SiteBook />} />
            <Route path="/for-coaches" element={<SiteForCoaches />} />
            <Route path="/policies" element={<Policies />} />
            <Route path="/academy/subscription" element={<ProtectedRoute><AcademySubscriptionPage /></ProtectedRoute>} />
            <Route path="/academy/programs" element={<ProtectedRoute><AcademyProgramsPage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
            {/* Public */}
            <Route path="/coach/:id" element={<SiteCoachProfile />} />
            <Route path="/coach/:id/classic" element={<CoachPublicProfile />} />
            <Route path="/academy/:id" element={<AcademyPublicProfile />} />
            <Route path="/find-coaches" element={<SiteSearch />} />
            <Route path="/coaches-map" element={<CoachesMap />} />
            <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
            <Route path="/goalkeeper-coaches" element={<GoalkeeperCoaches />} />
            {/* Root: Landing for guests, Dashboard for authed */}
            <Route path="/" element={<RootRouter />} />
            <Route path="/dashboard" element={<ProtectedRoute><RoleHome /></ProtectedRoute>} />
            <Route path="/children" element={<ProtectedRoute><ChildrenPage /></ProtectedRoute>} />
            <Route path="/children/:id" element={<ProtectedRoute><ChildProfile /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/matches" element={<ProtectedRoute><MatchesPage /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsRouter /></ProtectedRoute>} />
            <Route path="/upgrade" element={<ProtectedRoute><UpgradeRedirect /></ProtectedRoute>} />
            <Route path="/bookings" element={<ProtectedRoute><BookingsPage /></ProtectedRoute>} />
            <Route path="/favorites" element={<ProtectedRoute><FavoritesPage /></ProtectedRoute>} />
            <Route path="/player/:id" element={<ProtectedRoute><PlayerProfile /></ProtectedRoute>} />
            <Route path="/claim/:token" element={<ClaimInvite />} />
            <Route path="/academy/coaches" element={<ProtectedRoute><AcademyCoachesPage /></ProtectedRoute>} />
            <Route path="/academy/applications" element={<ProtectedRoute><AcademyApplicationsPage /></ProtectedRoute>} />
            <Route path="/earnings" element={<ProtectedRoute><EarningsPage /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster theme="light" position="top-right" />
        </BrowserRouter>
      </SiteProvider>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
